import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import GetGatheringAndTopPhotosDocument from "src/api/graphql/__generated__/documents/GetGatheringAndTopPhotosDocument";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";

/**
 * By default, this delays its execution unless `forceActive` is set to true.
 */
const useGatheringTopPhotos = ({
	gatheringId,
	active = true,
}: {
	gatheringId: string;
	active?: boolean;
}) => {
	const { data, refetch } = useQuery(GetGatheringAndTopPhotosDocument, {
		fetchPolicy: "cache-first",
		variables: {
			input: {
				gatheringId,
			},
		},
		skip: !active,
	});
	const [initialLoading, setInitialLoading] = useState(true);
	const refreshingRef = useRef(false);
	useEffect(() => {
		refreshingRef.current = true;
		refetch({
			input: {
				gatheringId,
			},
		}).finally(() => {
			setInitialLoading(false);
		});
	}, [gatheringId, refetch]);

	const [refreshing, setRefreshing] = useState(false);
	const handleRefresh = useStabilizedCallback(async () => {
		if (refreshingRef.current) return;
		try {
			setRefreshing(true);
			refreshingRef.current = true;
			await refetch({
				input: {
					gatheringId,
				},
			});
		} finally {
			setRefreshing(false);
			refreshingRef.current = false;
		}
	}, [gatheringId, refetch]);
	const topPhotos = useMemo(() => {
		return (data?.getGathering?.topPhotos?.payload ?? []) as GatheringOrLocalPhoto[];
	}, [data?.getGathering?.topPhotos?.payload]);

	return {
		refresh: handleRefresh,
		queryLoading: initialLoading,
		topPhotos,
		refreshing,
	};
};
export default useGatheringTopPhotos;
